import { ActionIcon, Button, Textarea } from '@mantine/core';
import { modals } from '@mantine/modals';
import { IconEye, IconEyeOff, IconTrash } from '@tabler/icons-react';
import classNames from 'classnames';
import dayjs from 'dayjs';
import { useMemo, useState } from 'react';

import {
  useCreateAnnouncement,
  useDeleteAnnouncement,
  usePublishAnnouncement,
  useUnpublishAnnouncement,
} from 'src/Hooks/Mutations/useAnnouncementMutations';
import useAnnouncements from 'src/Hooks/Queries/useAnnouncements';
import { AnnouncementDto, AnnouncementLevel } from 'src/types/announcement.types';

import styles from './InternalBackofficePage.module.scss';

const LEVEL_OPTIONS: { value: AnnouncementLevel; label: string; pillClass: string }[] = [
  { value: AnnouncementLevel.Info, label: 'Info', pillClass: styles.levelPillInfo },
  { value: AnnouncementLevel.Warning, label: 'Maintenance', pillClass: styles.levelPillWarning },
  { value: AnnouncementLevel.Critical, label: 'Incident', pillClass: styles.levelPillCritical },
];

const levelBadgeClass: Record<AnnouncementLevel, string> = {
  [AnnouncementLevel.Info]: styles.levelBadgeInfo,
  [AnnouncementLevel.Warning]: styles.levelBadgeWarning,
  [AnnouncementLevel.Critical]: styles.levelBadgeCritical,
};

const previewBgClass: Record<AnnouncementLevel, string> = {
  [AnnouncementLevel.Info]: styles.levelPillInfo,
  [AnnouncementLevel.Warning]: styles.levelPillWarning,
  [AnnouncementLevel.Critical]: styles.levelPillCritical,
};

const InternalBackofficePage = () => {
  const { data: announcements } = useAnnouncements();
  const { mutate: create, isLoading: isCreating } = useCreateAnnouncement();
  const { mutate: publish } = usePublishAnnouncement();
  const { mutate: unpublish } = useUnpublishAnnouncement();
  const { mutate: deleteAnnouncement } = useDeleteAnnouncement();

  const [level, setLevel] = useState<AnnouncementLevel>(AnnouncementLevel.Info);
  const [message, setMessage] = useState('');

  const active = useMemo(() => announcements?.find(a => a.isActive), [announcements]);

  const handleSubmit = (publishNow: boolean) => {
    if (message.trim().length === 0) return;
    create(
      { level, message: message.trim(), publish: publishNow },
      {
        onSuccess: () => {
          setMessage('');
        },
      }
    );
  };

  const handleDelete = (announcement: AnnouncementDto) => {
    modals.openConfirmModal({
      title: "Supprimer l'annonce",
      children: <div>Cette action est irréversible. Confirmer ?</div>,
      labels: { confirm: 'Supprimer', cancel: 'Annuler' },
      confirmProps: { color: 'red' },
      onConfirm: () => deleteAnnouncement(announcement.id),
    });
  };

  return (
    <div className={styles.container}>
      <div className={styles.title}>Backoffice Share Heritage — Annonces</div>

      <section className={styles.section}>
        <div className={styles.sectionTitle}>Nouvelle annonce</div>
        <div className={styles.formRow}>
          <div className={styles.levelOptions}>
            {LEVEL_OPTIONS.map(option => (
              <button
                key={option.value}
                type="button"
                className={classNames(styles.levelPill, option.pillClass, {
                  [styles.selected]: level === option.value,
                })}
                onClick={() => setLevel(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
          <Textarea
            placeholder="Message affiché dans le bandeau…"
            minRows={2}
            maxRows={5}
            autosize
            value={message}
            onChange={event => setMessage(event.currentTarget.value)}
            maxLength={500}
          />
          <div>
            <div className={styles.previewLabel}>Aperçu</div>
            <div className={styles.previewContainer}>
              {message.trim().length > 0 ? (
                <div
                  className={classNames(styles.levelPill, previewBgClass[level])}
                  style={{
                    borderRadius: 0,
                    width: '100%',
                    justifyContent: 'center',
                    padding: '10px 16px',
                    fontWeight: 500,
                  }}
                >
                  {message}
                </div>
              ) : (
                <div style={{ padding: '12px 16px' }} className={styles.emptyMessage}>
                  Saisissez un message pour voir l'aperçu
                </div>
              )}
            </div>
          </div>
          <div className={styles.formActions}>
            <Button
              variant="outline"
              onClick={() => handleSubmit(false)}
              disabled={isCreating || message.trim().length === 0}
            >
              Enregistrer (brouillon)
            </Button>
            <Button
              onClick={() => handleSubmit(true)}
              disabled={isCreating || message.trim().length === 0}
            >
              Publier maintenant
            </Button>
          </div>
        </div>
      </section>

      <section className={styles.section}>
        <div className={styles.sectionTitle}>Annonces</div>
        {!announcements || announcements.length === 0 ? (
          <div className={styles.emptyMessage}>Aucune annonce pour le moment.</div>
        ) : (
          <div className={styles.list}>
            {announcements.map(announcement => (
              <div
                key={announcement.id}
                className={classNames(styles.item, {
                  [styles.active]: announcement.isActive,
                })}
              >
                <div className={styles.itemBody}>
                  <div className={styles.itemMeta}>
                    <span
                      className={classNames(styles.levelBadge, levelBadgeClass[announcement.level])}
                    >
                      {announcement.level}
                    </span>
                    {announcement.isActive && <span>● Active</span>}
                    <span>{dayjs(announcement.createdAt).format('DD/MM/YYYY HH:mm')}</span>
                  </div>
                  <div className={styles.itemMessage}>{announcement.message}</div>
                </div>
                <div className={styles.itemActions}>
                  {announcement.isActive ? (
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      onClick={() => unpublish(announcement.id)}
                      title="Dépublier"
                    >
                      <IconEyeOff size={18} />
                    </ActionIcon>
                  ) : (
                    <ActionIcon
                      variant="subtle"
                      color="green"
                      onClick={() => publish(announcement.id)}
                      title="Publier"
                      disabled={!!active}
                    >
                      <IconEye size={18} />
                    </ActionIcon>
                  )}
                  <ActionIcon
                    variant="subtle"
                    color="red"
                    onClick={() => handleDelete(announcement)}
                    title="Supprimer"
                  >
                    <IconTrash size={18} />
                  </ActionIcon>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default InternalBackofficePage;
